"use client"

/**
 * QuestionComposerInspector — the right rail: format, filing, difficulty, and
 * the delivery options. Same provider as the builder, so a host that mounts one
 * gets the other for free.
 */

import * as React from "react"

import { cn } from "@/lib/utils"
import { leadInPlainText } from "@/lib/lead-in-html"
import { initialsFromDisplayName } from "@/lib/initials-from-name"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { LIBRARY_ITEMS } from "@/lib/mock/library"
import { NAV_USER } from "@/lib/mock/navigation"
import { ASSESSMENT_BANK_QUESTIONS } from "@/lib/mock/assessment-add-questions"
import { bankQuestionFromLibraryItem } from "@/lib/question-preview-adapters"
import { questionPreview, type QuestionVersion } from "@/lib/question-preview"
import { relativeTime } from "@/lib/relative-time"
import { clarifyQuestionSheetEnhance } from "@/lib/leo-question-sheet-enhance"
import { LeoWorkStatus } from "@/components/assessment-structure/leo-work-status"
import { ExamCategoryPickerPanel } from "@/components/exam-category-picker-panel"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Tip } from "@/components/ui/tip"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form"
import { LeoApplyField } from "@/components/question-quality/leo-apply-motion"
import {
  LeoSpottedClassificationApply,
  LeoSpottedFindingsCard,
} from "@/components/question-quality/question-leo-jobs"
import { LeoSpottedLevelFields } from "@/components/question-quality/leo-spotted-level-fields"
import { LeoSpottedQueueCard } from "@/components/question-quality/leo-spotted-queue-card"
import { useLeoSpottedQueue } from "@/lib/question-quality/use-leo-spotted-queue"
import type { LeoSpottedCard } from "@/lib/question-quality/leo-spotted-queue"
import {
  leoSpottedSubjectFromComposer,
  readLeoSpotted,
} from "@/lib/question-quality/leo-spotted-source"
import { ComposerPanelCard } from "@/components/templates/composer-panel-card"
import { CLASSIFICATION_IDENTIFIED_CHECK_ID } from "@/lib/question-quality"
import {
  AUTHORING_DEFAULT_POINTS,
  AUTHORING_INSPECTOR_GENERAL_TOGGLES,
  AUTHORING_MULTI_SELECT_SCORING_OPTIONS,
  AUTHORING_QUESTION_TYPES,
  authoringInspectorToggleVisible,
  resolveUnlimitedSelections,
  unlimitedSelectionsPolicy,
  type AuthoringMultiSelectScoring,
} from "@/lib/library-authoring"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import {
  COMPOSER_FIELD_LABEL_CLASS,
  CategoryPickerControl,
  ComposerTagsField,
  DifficultyMeter,
  FolderPickerControl,
  FormatPickerControl,
  InspectorSection,
  InspectorSubsectionLabel,
  InspectorToggleRow,
  MarksInputStepper,
} from "./composer-fields"
import { useQuestionComposerContext, useQuestionComposerInspectorSurface, useQuestionComposerPageScroll } from "./composer-context"

function ComposerCategoryNav({
  value,
  onChange,
  onBack,
}: {
  value: string[]
  onChange: (ids: string[]) => void
  onBack: () => void
}) {
  const { categories, tree } = useExamCategories()
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="flex shrink-0 items-center gap-0.5">
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="shrink-0 text-muted-foreground"
          aria-label="Back to properties"
          onClick={onBack}
        >
          <i className="fa-light fa-arrow-left text-sm" aria-hidden="true" />
        </Button>
        <h2 className="m-0 text-sm font-semibold text-foreground">Category</h2>
      </div>
      <ExamCategoryPickerPanel
        categories={categories}
        tree={tree}
        value={value}
        multiple
        onChange={onChange}
        className="min-h-0 flex-1"
      />
    </div>
  )
}

/** Module scope: Ask Leo replaces the inspector, so component state is lost before Apply. */
const PENDING_SPOTTED_CARDS = new Map<string, { card: LeoSpottedCard; print: string }>()

export function QuestionInspectorCompactNav({
  density = "rail",
}: {
  density?: "rail" | "card"
}) {
  const {
    hasOpenReviewChecks,
    openLeoSpotted,
    openProperties,
    showLiveLeoSpotted,
  } = useQuestionComposerContext()
  const rail = density === "rail"
  const buttonClass = rail
    ? "size-9 shrink-0 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
    : cn(
        "size-11 shrink-0 rounded-xl border-border shadow-sm",
        "bg-[var(--secondary-panel-bg)] text-sidebar-foreground",
        "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )

  const leoButton = (
    <Tip label="Leo AI" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label={
          hasOpenReviewChecks ? "Leo AI. Review checks are open." : "Leo AI"
        }
        onClick={openLeoSpotted}
      >
        <span className="relative">
          <i
            className="fa-duotone fa-solid fa-star-christmas text-md leading-none text-brand"
            aria-hidden="true"
          />
          {hasOpenReviewChecks ? (
            <span
              className="absolute -right-1 -top-1 size-1.5 rounded-full bg-destructive"
              aria-hidden="true"
            />
          ) : null}
        </span>
      </Button>
    </Tip>
  )

  const propertiesButton = (
    <Tip label="Properties" side="left">
      <Button
        type="button"
        size={rail ? "icon" : "icon-lg"}
        variant={rail ? "ghost" : "outline"}
        className={buttonClass}
        aria-label="Properties"
        onClick={openProperties}
      >
        <i className="fa-light fa-sliders text-md leading-none" aria-hidden="true" />
      </Button>
    </Tip>
  )

  if (rail) {
    return (
      <>
        {showLiveLeoSpotted ? (
          <li className="flex w-full justify-center" role="none">
            {leoButton}
          </li>
        ) : null}
        <li className="flex w-full justify-center" role="none">
          {propertiesButton}
        </li>
      </>
    )
  }

  return (
    <div className="flex w-full flex-col items-center gap-1 py-1">
      {showLiveLeoSpotted ? leoButton : null}
      {propertiesButton}
    </div>
  )
}

export function QuestionComposerInspectorFields() {
  const {
    form,
    inspectorLead,
    leoSpottedLead,
    importLeoFixedOnly,
    inspectorPanel,
    capabilities,
    disabled,
    folders,
    onRequestNewFolder,
    onReadyChange,
    leoJobs,
    composeSpotted,
    showLiveLeoSpotted,
    blueprintExplain,
    qualityChecks,
    qualitySnapshot,
    historyQuestionId,
    applyQualityCheck,
    leoWorking,
    enhanceWithLeo,
    setLeoVariationMode,
    setLeoBriefOpen,
    folderPickerOpen,
    setFolderPickerOpen,
    categoryPickerOpen,
    setCategoryPickerOpen,
    setDifficultyMode,
    difficultyInsight,
    hideDiscrimination,
    tagDraft,
    setTagDraft,
    watchedType,
    watchedTags,
    watchedStatus,
    changeType,
    commitTag,
    removeTag,
  } = useQuestionComposerContext()

  const composerCategoryIds = form.watch("categoryIds")
  const isReady = watchedStatus === "in_review"
  const showMultiSelectScoring =
    watchedType === "mcq_multiple" || watchedType === "matching"

  const formatOptions = React.useMemo(
    () =>
      AUTHORING_QUESTION_TYPES.filter((option) => {
        if (option.value === watchedType) return true
        if (option.value === "numeric") return capabilities.numeric
        if (option.value === "fill_blank") return capabilities.fillBlank
        if (option.value === "matching") return capabilities.matchingPairs
        if (option.value === "ordering") return capabilities.ordering
        if (option.value === "hotspot") return capabilities.hotspot
        return true
      }),
    [capabilities, watchedType],
  )

  const visibleGeneralToggles = AUTHORING_INSPECTOR_GENERAL_TOGGLES.filter(
    (option) => authoringInspectorToggleVisible(option, watchedType),
  )
  const identifiedClassification = qualityChecks.filter(
    (check) => check.id === CLASSIFICATION_IDENTIFIED_CHECK_ID,
  )
  React.useLayoutEffect(() => {
    const leo = document.getElementById("composer-leo-spotted")
    const scroller =
      leo?.closest<HTMLElement>("[data-composer-inspector-scroll]") ?? null
    if (!scroller) return
    const pin = () => {
      scroller.scrollTop = 0
    }
    pin()
    const frame = window.requestAnimationFrame(pin)
    return () => window.cancelAnimationFrame(frame)
  }, [blueprintExplain?.title, leoJobs.length])

  const panelScrollReady = React.useRef(false)
  React.useEffect(() => {
    if (!panelScrollReady.current) {
      panelScrollReady.current = true
      return
    }
    const id =
      inspectorPanel === "leo"
        ? "composer-leo-spotted"
        : "composer-properties"
    const target = document.getElementById(id)
    const scroller = target?.closest<HTMLElement>(
      "[data-composer-inspector-scroll]",
    )
    if (!target || !scroller) return
    const top = target.offsetTop - scroller.offsetTop
    scroller.scrollTo({ top: Math.max(0, top) })
  }, [inspectorPanel])

  const openSpottedIssues = composeSpotted?.issues.filter((issue) => composeSpotted.openIds.has(issue.id)) ?? []

  /**
   * Leo spotted as a queue rather than a stacked list (PRD 2.2.1).
   *
   * The findings are the same ones the old card listed; what changes is that
   * the author meets them one at a time in pedagogical order, with Skip for
   * "not now" and Dismiss for "this is not a defect".
   */
  // Live form state, read by the same engine the bank row and the sheet
  // use, so the composer cannot say Looks good about a question the table
  // is badging Needs fixing.
  const spottedReading = readLeoSpotted(
    leoSpottedSubjectFromComposer({
      questionId: historyQuestionId,
      title: qualitySnapshot.title,
      stem: leadInPlainText(qualitySnapshot.leadIn),
      type: qualitySnapshot.type,
      options: qualitySnapshot.options,
      rationale: qualitySnapshot.rationale,
      bloom: qualitySnapshot.bloom,
      difficulty: qualitySnapshot.difficulty,
      categoryIds: qualitySnapshot.categoryIds,
    }),
  )
  // Named so the dependency array below holds values rather than
  // expressions: an expression there is invisible to the compiler and
  // to anyone reading it.
  const spottedIssueIdKey = spottedReading.issues.map((issue) => issue.id).join("|")
  const snapshotCategoryCount = qualitySnapshot.categoryIds?.length ?? 0

  const spottedQueueInput = React.useMemo(
    () => ({
      // Findings the compose session already knows about keep their own
      // targeted instruction; the rest come from the shared reading.
      issues: spottedReading.issues.map((issue) => {
        const live = openSpottedIssues.find((row) => row.id === issue.id)
        return live
          ? {
              ...issue,
              outcome: issue.outcome ?? live.outcome,
              instruction: issue.instruction ?? live.instruction,
            }
          : issue
      }),
      bloom: qualitySnapshot.bloom,
      difficulty: qualitySnapshot.difficulty,
      categoryIds: qualitySnapshot.categoryIds,
      resolvedCount: spottedReading.resolvedCount,
      // Answered findings stay in the stack behind the open ones.
      resolvedIssues: spottedReading.resolvedIssues,
      edited: spottedReading.edited,
      tag:
        spottedReading.tag === "worth-a-look"
          ? ("worth-a-look" as const)
          : spottedReading.tag
            ? ("needs-fixing" as const)
            : null,
    }),
    // `openSpottedIssues` is rebuilt every render; its ids are the content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      spottedIssueIdKey,
      spottedReading.tag,
      spottedReading.resolvedCount,
      spottedReading.resolvedIssues,
      spottedReading.edited,
      qualitySnapshot.bloom,
      qualitySnapshot.difficulty,
      snapshotCategoryCount,
    ],
  )
  const spottedQueue = useLeoSpottedQueue(
    historyQuestionId ?? "composer-draft",
    spottedQueueInput,
  )

  /**
   * A card counts as answered once the field it covers changes, not when
   * its button is pressed. Pressing only asks Leo; the author can still
   * discard the reply, and a finding recorded on the click stayed resolved
   * over an unchanged question.
   */
  const spottedFieldPrint = (dimension: LeoSpottedCard["dimension"]) => {
    const stem = leadInPlainText(qualitySnapshot.leadIn).trim()
    const choices = qualitySnapshot.options.map((option) => `${option.text}|${option.isCorrect}`).join("\n")
    switch (dimension) {
      case "stem":
        return `${stem}\n${qualitySnapshot.type}`
      case "distractors":
        return choices
      case "rationale":
        return qualitySnapshot.rationale ?? ""
      case "metadata":
        return `${qualitySnapshot.bloom}|${qualitySnapshot.difficulty}`
      case "category":
        return (qualitySnapshot.categoryIds ?? []).join(",")
      default:
        return `${stem}\n${choices}\n${qualitySnapshot.rationale ?? ""}`
    }
  }
  const pendingKey = historyQuestionId ?? "composer-draft"
  const pendingSpotted = PENDING_SPOTTED_CARDS.get(pendingKey) ?? null
  const pendingPrintNow = pendingSpotted ? spottedFieldPrint(pendingSpotted.card.dimension) : null
  React.useEffect(() => {
    if (!pendingSpotted || pendingPrintNow === pendingSpotted.print) return
    PENDING_SPOTTED_CARDS.delete(pendingKey)
    spottedQueue.resolve(pendingSpotted.card)
  }, [pendingSpotted, pendingPrintNow, pendingKey, spottedQueue])

  /** The queue's sparkle CTA, routed to the handler that card implies. */
  function runSpottedCard(card: LeoSpottedCard) {
    switch (card.kind) {
      case "overuse":
        setLeoVariationMode(true)
        setLeoBriefOpen(true)
        break
      case "category":
      case "metadata":
        enhanceWithLeo(
          undefined,
          "Suggest the best classification for this question, including category, Bloom's level, and difficulty. Explain why and let me review before applying.",
        )
        break
      case "performance":
        enhanceWithLeo(
          undefined,
          "Diagnose why this question performed poorly and propose a fix I can review before applying.",
        )
        break
      default:
        // The card's own instruction names the one thing to change. The
        // fallback behind it asks for a whole-question rewrite, which is
        // what used to answer "Write rationale" with a new stem and new
        // choices and no rationale.
        enhanceWithLeo(
          undefined,
          card.instruction ??
            `Rewrite this question to resolve all open findings while keeping its learning objective and answer format: ${card.issues
              .map((issue) => issue.label)
              .join("; ")}.`,
        )
    }
    if (card.selfClearing) return
    PENDING_SPOTTED_CARDS.set(pendingKey, {
      card,
      print: spottedFieldPrint(card.dimension),
    })
  }

  const classificationMissing = openSpottedIssues.some((issue) => /classification|category|bloom|difficulty/i.test(`${issue.id} ${issue.label}`))
  const stemFinding = openSpottedIssues.some((issue) => issue.cover === "stem")
  const choiceFinding = openSpottedIssues.some((issue) => issue.cover === "choices")
  const choiceFitFinding = openSpottedIssues.some((issue) => issue.id === "choices-fit")
  const multiSpottedClarify = openSpottedIssues.length > 1 ? {
    question: "What should Leo change in this question?",
    choices: [
      { id: "all", label: "Fix all open findings", instruction: `Rewrite this question to resolve all open findings while keeping its learning objective and answer format: ${openSpottedIssues.map((issue) => issue.label).join("; ")}.` },
      ...(classificationMissing ? [{ id: "classification", label: "Suggest classification", instruction: "Suggest the best category, Bloom's level, and difficulty for this question. Explain the reasons and let me review before applying." }] : []),
      ...(stemFinding ? [{ id: "stem", label: "Change the stem only", instruction: "Tighten the stem. Leave the choices for a next step if they no longer fit." }] : []),
      ...(choiceFitFinding
        ? [{ id: "choices", label: "Harmonize the choices only", instruction: "Harmonize the choices to the new question. Keep each choice in its current letter." }]
        : choiceFinding ? [{ id: "choices", label: "Change the choices only", instruction: "Write plausible answer choices for this stem" }] : []),
    ],
  } : null

  const bloomMissing = !qualitySnapshot.bloom?.trim()
  const difficultyMissing = !qualitySnapshot.difficulty?.trim()
  const spottedLevelFields =
    bloomMissing || difficultyMissing ? (
      <LeoSpottedLevelFields
        stem={leadInPlainText(qualitySnapshot.leadIn)}
        type={qualitySnapshot.type}
        showBloom={bloomMissing}
        showDifficulty={difficultyMissing}
        disabled={disabled}
        onApply={(next) => {
          if (next.bloom) {
            form.setValue("bloom", next.bloom, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
          if (next.difficulty) {
            form.setValue("difficulty", next.difficulty, {
              shouldDirty: true,
              shouldValidate: true,
            })
            setDifficultyMode("manual")
          }
          const metadata = spottedQueue.queue.cards.find(
            (card) => card.kind === "metadata" && card.state === "open",
          )
          if (metadata) spottedQueue.resolve(metadata)
        }}
        onSkip={() => {
          const metadata = spottedQueue.queue.cards.find(
            (card) => card.kind === "metadata" && card.state === "open",
          )
          if (metadata) spottedQueue.skip(metadata)
        }}
      />
    ) : null

  if (categoryPickerOpen) {
    return (
      <ComposerCategoryNav
        value={composerCategoryIds ?? []}
        onChange={(ids) =>
          form.setValue("categoryIds", ids, { shouldDirty: true, shouldValidate: true })
        }
        onBack={() => setCategoryPickerOpen(false)}
      />
    )
  }

  return (
    <fieldset disabled={disabled} className="contents">
          <div id="composer-leo-spotted" className="flex flex-col gap-3">
            {leoWorking ? (
              <LeoWorkStatus label={leoWorking} />
            ) : null}
            {leoSpottedLead}
            {showLiveLeoSpotted &&
            !importLeoFixedOnly &&
            identifiedClassification[0] &&
            !composeSpotted?.issues.some((issue) => issue.cover === "category" || issue.cover === "bloom" || issue.cover === "difficulty") ? (
              <LeoSpottedClassificationApply
                check={identifiedClassification[0]}
                snapshot={qualitySnapshot}
                disabled={disabled}
                applying={Boolean(leoWorking)}
                onApply={(check, alternate) =>
                  applyQualityCheck(check, false, alternate)
                }
              />
            ) : null}
            {!showLiveLeoSpotted ? null : composeSpotted ? (
              <LeoSpottedQueueCard
                queue={spottedQueue.queue}
                tag={spottedReading.tag}
                onNavigate={spottedQueue.navigate}
                onPrimary={runSpottedCard}
                onSkip={spottedQueue.skip}
                onReopen={spottedQueue.reopen}
                levelFields={spottedLevelFields}
                fixing={Boolean(leoWorking)}
                fixingLabel={leoWorking ?? undefined}
              />
            ) : importLeoFixedOnly ||
              !(identifiedClassification.length > 0 || leoSpottedLead) ? (
              // Import review is the same editor with the same right rail,
              // so it gets the same card. It used to fall through to the
              // old stacked panel here purely because `composeSpotted` is
              // null on a freshly extracted question, which left import
              // showing one Leo spotted and compose showing another.
              <LeoSpottedQueueCard
                queue={spottedQueue.queue}
                tag={spottedReading.tag}
                onNavigate={spottedQueue.navigate}
                onPrimary={runSpottedCard}
                onSkip={spottedQueue.skip}
                onReopen={spottedQueue.reopen}
                disabled={disabled}
                levelFields={spottedLevelFields}
                fixing={Boolean(leoWorking)}
                fixingLabel={leoWorking ?? undefined}
              />
            ) : null}
          </div>
      <InspectorSection title="Properties">
      <div id="composer-properties" className="flex flex-col gap-6">
        {onReadyChange ? (
          <div className="flex items-center justify-between gap-3 border-b border-border pb-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1.5">
              <Label
                htmlFor="qb-mark-ready"
                className="text-sm font-medium text-foreground"
              >
                Published
              </Label>
              <p className="text-xs leading-snug text-muted-foreground">
                {isReady
                  ? "This question can go on an assessment."
                  : "Draft until you turn this on."}
              </p>
            </div>
            <ToggleSwitch
              id="qb-mark-ready"
              checked={isReady}
              onChange={onReadyChange}
            />
          </div>
        ) : null}
        {inspectorLead}
        <div className="flex flex-col gap-4">
          <FormField
            control={form.control}
            name="type"
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <Label htmlFor="qb-format" className={COMPOSER_FIELD_LABEL_CLASS}>
                  Question format
                </Label>
                <FormControl>
                  <FormatPickerControl
                    id="qb-format"
                    options={formatOptions}
                    value={field.value}
                    onChange={(v) => changeType(v)}
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="folderIds"
            render={({ field }) => (
              <FormItem className="gap-1.5">
                <Label className={COMPOSER_FIELD_LABEL_CLASS}>Locations</Label>
                <FormControl>
                  <FolderPickerControl
                    folders={folders}
                    value={field.value}
                    onChange={(v) => field.onChange(v)}
                    open={folderPickerOpen}
                    onOpenChange={setFolderPickerOpen}
                    onRequestNewFolder={() => {
                      setFolderPickerOpen(false)
                      onRequestNewFolder?.()
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        {capabilities.scoring ? (
        <InspectorSection title="Scoring">
          <div className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="points"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <Label
                      htmlFor="qb-points"
                      className={cn(COMPOSER_FIELD_LABEL_CLASS, "shrink-0")}
                    >
                      Marks
                    </Label>
                    <FormControl>
                      <MarksInputStepper
                        id="qb-points"
                        value={field.value}
                        placeholder={AUTHORING_DEFAULT_POINTS}
                        disabled={disabled}
                        onChange={field.onChange}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {showMultiSelectScoring ? (
              <FormField
                control={form.control}
                name="multiSelectScoring"
                render={({ field }) => {
                  const unlimitedPolicy = unlimitedSelectionsPolicy(field.value)
                  return (
                    <FormItem className="gap-2">
                      <Label className={COMPOSER_FIELD_LABEL_CLASS}>
                        Credit mode
                      </Label>
                      <FormControl>
                        <RadioGroup
                          value={field.value}
                          onValueChange={(value) => {
                            const mode = value as AuthoringMultiSelectScoring
                            field.onChange(mode)
                            form.setValue(
                              "unlimitedSelections",
                              resolveUnlimitedSelections(
                                mode,
                                form.getValues("unlimitedSelections"),
                              ),
                              { shouldDirty: true },
                            )
                          }}
                          className="grid gap-2"
                          disabled={disabled}
                        >
                          {AUTHORING_MULTI_SELECT_SCORING_OPTIONS.map(
                            (option) => {
                              const selected = field.value === option.value
                              return (
                                <Label
                                  key={option.value}
                                  htmlFor={`qb-score-${option.value}`}
                                  className={cn(
                                    "flex cursor-pointer items-start gap-3 rounded-2 border p-3 transition-colors",
                                    selected
                                      ? "border-brand bg-brand/10"
                                      : "border-border hover:bg-muted/40",
                                    disabled && "cursor-not-allowed opacity-60",
                                  )}
                                >
                                  <RadioGroupItem
                                    value={option.value}
                                    id={`qb-score-${option.value}`}
                                    className="mt-0.5"
                                    disabled={disabled}
                                  />
                                  <span className="min-w-0">
                                    <span className="block text-sm font-medium text-foreground">
                                      {option.label}
                                    </span>
                                    <span className="mt-0.5 block text-xs text-muted-foreground">
                                      {option.description}
                                    </span>
                                  </span>
                                </Label>
                              )
                            },
                          )}
                        </RadioGroup>
                      </FormControl>
                      <FormField
                        control={form.control}
                        name="unlimitedSelections"
                        render={({ field: unlimitedField }) => {
                          const locked = unlimitedPolicy !== "optional"
                          return (
                            <InspectorToggleRow
                              id="qb-unlimitedSelections"
                              variant="inset"
                              label="Allow unlimited selections"
                              description={
                                unlimitedPolicy === "forced_off"
                                  ? "Off for Partial credit. Students cannot select more options than there are correct answers."
                                  : unlimitedPolicy === "forced_on"
                                    ? "On for +/- Partial credit so wrong selections can be deducted."
                                    : "When on, students may select any number of options."
                              }
                              checked={unlimitedField.value}
                              onCheckedChange={(checked) => {
                                if (locked) return
                                unlimitedField.onChange(checked)
                              }}
                              disabled={disabled || locked}
                            />
                          )
                        }}
                      />
                      <FormMessage />
                    </FormItem>
                  )
                }}
              />
            ) : null}
            {visibleGeneralToggles.map((option) => (
              <FormField
                key={option.key}
                control={form.control}
                name={option.key}
                render={({ field }) => (
                  <InspectorToggleRow
                    id={`qb-${option.key}`}
                    variant="inset"
                    label={option.label}
                    description={option.description}
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={disabled}
                  />
                )}
              />
            ))}
          </div>
        </InspectorSection>
        ) : null}

        <InspectorSection title="Tags">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="qb-tag-input" className={COMPOSER_FIELD_LABEL_CLASS}>
              Tags
            </Label>
            <ComposerTagsField
              id="qb-tag-input"
              tags={watchedTags}
              draft={tagDraft}
              disabled={disabled}
              onDraftChange={setTagDraft}
              onCommit={commitTag}
              onRemove={removeTag}
            />
          </div>
        </InspectorSection>

        <InspectorSection title="Classification">
          <div id="composer-classification" className="flex flex-col gap-4">
            <FormField
              control={form.control}
              name="categoryIds"
              render={({ field }) => (
                <FormItem className="gap-1.5">
                  <InspectorSubsectionLabel>Category</InspectorSubsectionLabel>
                  <FormControl>
                    <LeoApplyField anchor="category" revealBlobs>
                      <CategoryPickerControl
                        value={field.value}
                        onChange={(ids) => field.onChange(ids)}
                        open={categoryPickerOpen}
                        onOpenChange={setCategoryPickerOpen}
                        disabled={disabled}
                        navigateInPanel
                      />
                    </LeoApplyField>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="difficulty"
              render={({ field }) => (
                <DifficultyMeter
                  value={field.value}
                  onChange={(v) => {
                    field.onChange(v)
                    setDifficultyMode("manual")
                  }}
                  insight={difficultyInsight}
                  bloom={form.watch("bloom")}
                  onBloomChange={(v) =>
                    form.setValue("bloom", v, {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  hideDiscrimination={hideDiscrimination}
                />
              )}
            />
          </div>
        </InspectorSection>
        <ComposerAuthorHistory questionId={historyQuestionId} />
      </div>
      </InspectorSection>
    </fieldset>
  )
}

function composerProvenance(questionId?: string): {
  name: string
  email?: string
  versions: QuestionVersion[]
} {
  const signedIn = { name: NAV_USER.name, email: NAV_USER.email, versions: [] }
  if (!questionId) return signedIn
  const library = LIBRARY_ITEMS.find(
    (item) => item.id === questionId || item.questionId === questionId,
  )
  if (library) {
    const row = bankQuestionFromLibraryItem(library)
    const name = row.author || library.author || NAV_USER.name
    return {
      name,
      email:
        row.authorEmail ||
        library.authorEmail ||
        (name === NAV_USER.name ? NAV_USER.email : undefined),
      versions: questionPreview(row).versions,
    }
  }
  const bank = ASSESSMENT_BANK_QUESTIONS.find(
    (row) => row.id === questionId || row.id === questionId.replace(/^ai-case-/, ""),
  )
  if (!bank) return signedIn
  return {
    name: bank.author || NAV_USER.name,
    email: bank.authorEmail || (bank.author === NAV_USER.name ? NAV_USER.email : undefined),
    versions: questionPreview(bank).versions,
  }
}

function ComposerAuthorHistory({ questionId }: { questionId?: string }) {
  const provenance = composerProvenance(questionId)
  const [historyOpen, setHistoryOpen] = React.useState(false)
  const current = provenance.versions.find((version) => version.current) ?? provenance.versions[0]
  const updated = current ? relativeTime(current.date) : undefined
  const hasHistory = provenance.versions.length > 1
  const body = (
    <>
      <span className="flex min-w-0 flex-1 items-center gap-2.5 text-left">
        <Avatar shape="circle" className="shrink-0">
          <AvatarFallback className="text-xs font-semibold">
            {initialsFromDisplayName(provenance.name).toUpperCase()}
          </AvatarFallback>
        </Avatar>
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-sm font-medium leading-5 text-foreground">
            {provenance.name}
          </span>
          {provenance.email ? (
            <span className="truncate text-sm leading-5 text-muted-foreground">
              {provenance.email}
            </span>
          ) : null}
        </span>
      </span>
      <span className="flex shrink-0 items-center gap-2">
        {current ? (
          <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-xs leading-4 text-muted-foreground">
            <span className="font-medium tabular-nums text-foreground">v{current.number}</span>
            <span aria-hidden>·</span>
            <span>{updated?.compact ?? "Unknown"}</span>
          </span>
        ) : null}
        {hasHistory ? (
          <i className="fa-light fa-chevron-right text-xs text-muted-foreground" aria-hidden />
        ) : null}
      </span>
    </>
  )

  if (historyOpen) {
    return (
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={() => setHistoryOpen(false)}
          className="flex w-fit items-center gap-1.5 text-sm font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <i className="fa-light fa-arrow-left text-xs" aria-hidden />
          Version history
        </button>
        <ol className="flex flex-col">
          {provenance.versions.map((version, index) => {
            const when = relativeTime(version.date)
            const isLast = index === provenance.versions.length - 1
            return (
              <li key={version.number} className="flex gap-3">
                <div className="flex w-2 shrink-0 flex-col items-center pt-2.5" aria-hidden>
                  <span className={cn("size-2 shrink-0 rounded-full", version.current ? "bg-brand-1" : "bg-border")} />
                  {isLast ? null : <span className="mt-1 w-px flex-1 bg-border" />}
                </div>
                <div className="mb-1 min-w-0 flex-1 py-1.5">
                  <p className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-xs leading-4 text-muted-foreground">
                    <span className="font-medium tabular-nums text-foreground">v{version.number}</span>
                    <span>·</span>
                    <span>{when?.compact ?? "Unknown"}</span>
                    {version.current ? (
                      <>
                        <span>·</span>
                        <span>Current</span>
                      </>
                    ) : null}
                  </p>
                  {version.note ? (
                    <p className="mt-1 text-sm leading-5 text-foreground">{version.note}</p>
                  ) : null}
                </div>
              </li>
            )
          })}
        </ol>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-foreground">Creator and history</p>
      {hasHistory ? (
        <button
          type="button"
          onClick={() => setHistoryOpen(true)}
          aria-label={`Version history, ${provenance.versions.length} versions. Showing version ${current?.number ?? 1}.`}
          className="flex w-full items-center gap-3 rounded-2 border border-border p-3 text-left transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {body}
        </button>
      ) : (
        <div className="flex w-full items-center gap-3 rounded-2 border border-border p-3">{body}</div>
      )}
    </div>
  )
}

export function QuestionComposerInspector() {
  const { form, inspectorOpen, setInspectorOpen } = useQuestionComposerContext()
  const pageScroll = useQuestionComposerPageScroll()
  const inspectorSurface = useQuestionComposerInspectorSurface()

  if (inspectorSurface === "secondary-panel") {
    return null
  }

  const hideInspectorButton = (
    <Tip side="bottom" label="Collapse to icons">
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        onClick={() => setInspectorOpen(false)}
        aria-label="Collapse to icons"
        aria-expanded={true}
      >
        <i className="fa-light fa-angles-right" aria-hidden="true" />
      </Button>
    </Tip>
  )

  return (
    <Form {...form}>
      {!inspectorOpen ? (
        <QuestionInspectorCompactNav density="card" />
      ) : (
        <div className="flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <ComposerPanelCard
            pageScrollBody={pageScroll}
            fillHeight
            headerActions={hideInspectorButton}
          >
            <QuestionComposerInspectorFields />
          </ComposerPanelCard>
        </div>
      )}
    </Form>
  )
}
